import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
} from '../hooks/useNotifications'
import { hasTokens } from '../lib/auth'
import { openNotificationsStream, type NotificationRow } from '../lib/api'

const ICONS: Record<string, string> = {
  new_match: '🎯',
  sopralluogo_richiesto: '📅',
  sopralluogo_fissato: '✅',
  sollecito_custode: '⏰',
  asta_imminente: '🚨',
  partecipazione_aggiudicata: '🎉',
  partecipazione_persa: '❌',
  new_client: '👤',
  new_participation: '📋',
  lotto_mutuabile: '🏦',
  generic: '🔔',
}

// Beep di notifica via WebAudio (niente asset da caricare). L'AudioContext può
// partire "suspended" finché l'utente non interagisce con la pagina: resume()
// va a buon fine dopo il primo gesto, quindi i suoni arrivano una volta che
// l'utente sta usando l'app. Errori (autoplay bloccato) ignorati.
let audioCtx: AudioContext | null = null
function playPing() {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext
    if (!Ctx) return
    if (!audioCtx) audioCtx = new Ctx()
    const ctx = audioCtx
    if (ctx.state === 'suspended') void ctx.resume()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, ctx.currentTime)
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.09)
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32)
    osc.start()
    osc.stop(ctx.currentTime + 0.33)
  } catch {
    // autoplay bloccato / WebAudio non disponibile → silenzioso
  }
}

function relativeTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'ora'
  if (min < 60) return `${min} min fa`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h fa`
  const day = Math.floor(hr / 24)
  if (day === 1) return 'ieri'
  if (day < 7) return `${day}g fa`
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}`
}

export function NotificationsBell() {
  // Campanello = commenti + eventi (match/sopralluoghi/aste/solleciti/…).
  // Le chat di team NON sono qui: stanno nella bolla "Messaggi" (l'endpoint
  // notifiche esclude internal_message).
  const { data } = useNotifications(false, 10, 'all')
  const markRead = useMarkRead()
  const markAll = useMarkAllRead('all')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const items: NotificationRow[] = data?.items ?? []
  const unread = data?.unread_count ?? 0
  const badge = unread > 9 ? '9+' : String(unread)

  // Click outside closes dropdown.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // SSE subscription. Live invalidates le query (badge + dropdown + chat) e
  // suona un beep all'arrivo di OGNI notifica (messaggi + eventi). Il guard su
  // created_at evita beep sul backlog iniziale alla connessione.
  useEffect(() => {
    if (!hasTokens()) return
    const controller = new AbortController()
    const startedAt = Date.now()
    void openNotificationsStream(
      controller.signal,
      (msg) => {
        void qc.invalidateQueries({ queryKey: ['notifications'] })
        // Nuovo messaggio interno → aggiorna lista chat + chat aperta in tempo
        // reale (no refresh manuale).
        if (msg.kind === 'internal_message') {
          void qc.invalidateQueries({ queryKey: ['conversations'] })
          void qc.invalidateQueries({ queryKey: ['messages'] })
        }
        const ts = new Date(msg.created_at).getTime()
        if (!Number.isFinite(ts) || ts >= startedAt - 5_000) playPing()
      },
      () => {
        // Network/auth error → fall back to polling already running on the query.
      },
    )
    return () => controller.abort()
  }, [qc])

  async function onItemClick(n: NotificationRow) {
    setOpen(false)
    if (!n.read) {
      try {
        await markRead.mutateAsync(n.id)
      } catch {
        // best-effort: still navigate
      }
    }
    routeFromNotification(n)
  }

  function routeFromNotification(n: NotificationRow) {
    const payload = (n.payload ?? {}) as Record<string, unknown>
    const apptId = typeof payload.appointment_id === 'string' ? payload.appointment_id : null
    const clientId = typeof payload.client_id === 'string' ? payload.client_id : null
    const propertyId = typeof payload.property_id === 'string' ? payload.property_id : null
    const partId = typeof payload.participation_id === 'string' ? payload.participation_id : null
    const commentId = typeof payload.comment_id === 'string' ? payload.comment_id : null
    const entityType = typeof payload.entity_type === 'string' ? payload.entity_type : null
    const entityId = typeof payload.entity_id === 'string' ? payload.entity_id : null

    // Sopralluogo-* → /sopralluoghi?openId=<appt>
    if (n.kind.startsWith('sopralluogo_') && apptId) {
      void navigate({ to: '/sopralluoghi', search: { openId: apptId } as never })
      return
    }

    // Callback esito post-sopralluogo → workflow della pratica (pagina detail).
    if (n.kind === 'callback_esito' && apptId) {
      void navigate({
        to: '/sopralluoghi/$id',
        params: { id: apptId },
        search: {} as never,
      })
      return
    }

    // Comment notifications use entity_type+entity_id.
    if (n.kind.startsWith('comment_') && entityType && entityId) {
      if (entityType === 'appointment') {
        void navigate({
          to: '/sopralluoghi',
          search: {
            openId: entityId,
            ...(commentId ? { comment: commentId } : {}),
          } as never,
        })
        return
      }
      if (entityType === 'client') {
        void navigate({
          to: '/clienti/$id',
          params: { id: entityId },
          search: (commentId ? { comment: commentId } : {}) as never,
        })
        return
      }
      if (entityType === 'property') {
        void navigate({
          to: '/lotti/$id',
          params: { id: entityId },
          search: (commentId ? { comment: commentId } : {}) as never,
        })
        return
      }
      if (entityType === 'participation') {
        void navigate({
          to: '/partecipazioni/$id',
          params: { id: entityId },
          search: (commentId ? { comment: commentId } : {}) as never,
        })
        return
      }
    }

    // Partecipazione outcomes.
    if (
      (n.kind === 'partecipazione_aggiudicata' ||
        n.kind === 'partecipazione_persa') &&
      partId
    ) {
      void navigate({ to: '/partecipazioni/$id', params: { id: partId } } as never)
      return
    }

    // Asta imminente / new_match → property detail.
    if ((n.kind === 'asta_imminente' || n.kind === 'new_match') && propertyId) {
      void navigate({ to: '/lotti/$id', params: { id: propertyId } } as never)
      return
    }

    // Fallback: client detail if client_id present.
    if (clientId) {
      void navigate({ to: '/clienti/$id', params: { id: clientId } } as never)
      return
    }

    // Last resort: parse target_url as plain navigation.
    if (n.target_url) {
      const [path, queryStr] = n.target_url.split('?')
      const searchObj: Record<string, string> = {}
      if (queryStr) {
        for (const pair of queryStr.split('&')) {
          const [k, v] = pair.split('=')
          if (k) searchObj[decodeURIComponent(k)] = decodeURIComponent(v ?? '')
        }
      }
      void navigate({ to: path, search: searchObj } as never)
    }
  }

  return (
    <div className="bell-wrap" ref={ref}>
      <button
        type="button"
        className="iconbtn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifiche"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9z" />
          <path d="M10 21a2 2 0 0 0 4 0" />
        </svg>
        {unread > 0 && <span className="num-badge">{badge}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          {items.length === 0 ? (
            <div className="notif-empty">Nessuna notifica</div>
          ) : (
            <>
              {items.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${n.read ? '' : 'unread'}`}
                  onClick={() => onItemClick(n)}
                >
                  <div className="notif-icon">{ICONS[n.kind] ?? '🔔'}</div>
                  <div className="notif-content">
                    <div className="notif-title">{n.title}</div>
                    {n.body && <div className="notif-body">{n.body}</div>}
                    <div className="notif-time">{relativeTime(n.created_at)}</div>
                  </div>
                </div>
              ))}
              {unread > 0 && (
                <div className="notif-footer">
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => markAll.mutate()}
                    disabled={markAll.isPending}
                  >
                    Segna tutte come lette
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
